// Helpers over backend-served scientific model metadata nodes.
import BenchlingConfig from 'BenchlingConfig';
import staticURL from 'lib/staticURL';

import type {ScientificModelMetadata} from './__generated/ScientificModelMetadata';
import {
  DEFAULT_STRUCTURE_PREDICTION_MODEL,
  LEGACY_DEFAULT_STRUCTURE_PREDICTION_MODEL,
  MAX_BULK_PREDICTION_ITEMS,
  MAX_MULTIMER_COMPONENTS,
  MSA_SUPPORT,
} from './ScientificModelConstants';

export type ScientificModelNode = ScientificModelMetadata;

export const MODEL_CATEGORY = {
  STRUCTURE_PREDICTION: 'structure_prediction',
  GENERATIVE: 'generative',
  PROPERTY_PREDICTION: 'property_prediction',
} as const;
export type ModelCategory = (typeof MODEL_CATEGORY)[keyof typeof MODEL_CATEGORY];

const MIN_AMINO_ACID_COUNT = 1;
const MAX_AMINO_ACID_COUNT = 1500;

export type ScientificModelsByExternalReference = ReadonlyMap<string, ScientificModelNode>;

export function formatModelCategoryLabel(category: string): string {
  return category.replace(/_/g, ' ');
}

export function indexModelsByExternalReference(
  models: ReadonlyArray<ScientificModelNode>
): ScientificModelsByExternalReference {
  const modelsByExternalReference = new Map<string, ScientificModelNode>();
  for (const model of models) {
    if (modelsByExternalReference.has(model.externalReference)) {
      throw new Error(
        `Scientific model registry contains duplicate external reference: ${model.externalReference}`
      );
    }
    modelsByExternalReference.set(model.externalReference, model);
  }
  return modelsByExternalReference;
}

export function requireScientificModel(
  models: ScientificModelsByExternalReference,
  externalReference: string
): ScientificModelNode {
  const model = models.get(externalReference);
  if (model == null) {
    throw new Error(`Unknown scientific model: ${externalReference}`);
  }
  return model;
}

export function getAvailableModelsByCategory(
  models: ReadonlyArray<ScientificModelNode>,
  category: ModelCategory
): ReadonlyArray<ScientificModelNode> {
  return models.filter((model) => model.category === category);
}

// Client-side because the default depends on a client feature flag.
export function getDefaultStructurePredictionModel(): string {
  return BenchlingConfig.MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS
    ? DEFAULT_STRUCTURE_PREDICTION_MODEL
    : LEGACY_DEFAULT_STRUCTURE_PREDICTION_MODEL;
}

// First available candidate, else the flag-based default, else the first available model.
export function resolveInitialStructurePredictionModel(
  availableModels: ReadonlyArray<ScientificModelNode>,
  ...candidates: Array<string | undefined>
): ScientificModelNode {
  for (const candidate of candidates) {
    const model = availableModels.find((availableModel) => availableModel.externalReference === candidate);
    if (model != null) {
      return model;
    }
  }
  const defaultModel = getDefaultStructurePredictionModel();
  const defaultModelNode = availableModels.find((model) => model.externalReference === defaultModel);
  if (defaultModelNode != null) {
    return defaultModelNode;
  }
  const firstAvailableModel = availableModels[0];
  if (firstAvailableModel == null) {
    throw new Error('Cannot select a scientific model because none are available');
  }
  return firstAvailableModel;
}

export function getModelIconUrl(model: ScientificModelNode): string {
  return staticURL(model.iconPath);
}

export function supportsMSA(model: ScientificModelNode): boolean {
  return model.capabilities.msaSupport !== MSA_SUPPORT.NONE;
}

export function getModelCapabilityLabels(model: ScientificModelNode): ReadonlyArray<string> {
  const labels = [model.capabilities.supportsMultimers ? 'multimers' : 'single chain'];
  if (model.capabilities.supportsMultimerMolecules) {
    labels.push('molecules');
  }
  return labels;
}

export function isMSAConfigurable(model: ScientificModelNode): boolean {
  return model.capabilities.msaSupport === MSA_SUPPORT.OPTIONAL;
}

// REQUIRED always uses MSAs; OPTIONAL follows the toggle; NONE omits the flag (null).
export function getUseMSAForSubmission(model: ScientificModelNode, msaEnabled: boolean): boolean | null {
  const msaSupport = model.capabilities.msaSupport;
  if (msaSupport === MSA_SUPPORT.REQUIRED) {
    return true;
  }
  if (msaSupport === MSA_SUPPORT.OPTIONAL) {
    return msaEnabled;
  }
  return null;
}

export function getMaxBulkPredictionItems(model: ScientificModelNode): number {
  return model.capabilities.maxBulkPredictionItems ?? MAX_BULK_PREDICTION_ITEMS;
}

export function getMinAminoAcidCount(model: ScientificModelNode): number {
  return model.capabilities.minAminoAcidCount ?? MIN_AMINO_ACID_COUNT;
}

export function getMaxAminoAcidCount(model: ScientificModelNode): number {
  return model.capabilities.maxAminoAcidCount ?? MAX_AMINO_ACID_COUNT;
}

export function getMaxMultimerComponents(model: ScientificModelNode): number {
  if (!model.capabilities.supportsMultimers) {
    return 1;
  }
  return model.capabilities.maxMultimerComponents ?? MAX_MULTIMER_COMPONENTS;
}

export function getMaxTokenCount(model: ScientificModelNode): number | null {
  return model.capabilities.maxTokenCount;
}
